/**
 * ArcGIS Pro–style classify / discrete / stretch / unique-value helpers.
 *
 * TiTiler accepts:
 *   - continuous ramps via `colormap_name` + `rescale` (Stretch)
 *   - interval classes via `colormap` JSON `[[[min,max],[r,g,b,a]], …]` (Classify / Discrete)
 *   - exact values via `colormap` JSON `{"12":"#…", …}` (Unique Values)
 *
 * Pure functions — no React, no fetch. The tool card builds breaks from
 * `/cog/statistics`, then {@link applyTitilerRasterStyleToTemplate} encodes them.
 */

import type { RasterColorRamp } from '@/state/useViewerStore';
import { getRampPreset } from './rasterSymbology';

/** How Stretch maps the data range onto the color scheme (ArcGIS Stretch Type). */
export type RasterStretchType = 'none' | 'minmax' | 'percent-clip' | 'stddev';

/** How Classify computes break points before the operator edits them. */
export type RasterClassifyMethod = 'equal-interval' | 'quantile' | 'manual';

export interface RasterClassBreak {
  readonly id: string;
  readonly upperValue: number;
  readonly color: string;
  readonly label: string;
  readonly visible: boolean;
}

export interface RasterUniqueValueRow {
  readonly id: string;
  readonly value: number;
  readonly color: string;
  readonly label: string;
  readonly visible: boolean;
}

export interface RasterBandStatsLike {
  readonly min: number;
  readonly max: number;
  readonly mean?: number;
  readonly std?: number;
  readonly percentile2?: number;
  readonly percentile98?: number;
  readonly counts?: readonly number[];
  readonly bins?: readonly number[];
  /** TiTiler `unique` — count of distinct values in the sample window. */
  readonly uniqueCount?: number;
}

/** RGB stops (0–1 position) for sampling discrete class colours from each ramp. */
const RAMP_RGB_STOPS: Readonly<Record<RasterColorRamp, readonly (readonly [number, number, number, number])[]>> = {
  // ArcGIS Pro Shaded Relief elevation scheme supplied by the operator:
  // pale-cyan coastal plains → lime/grass valleys → gold foothills →
  // terracotta ridges → alpine brown → slate rock → white snow summits.
  'arcgis-elevation': [
    [0, 200, 250, 237],
    [0.125, 226, 250, 137],
    [0.25, 0, 165, 38],
    [0.375, 212, 167, 0],
    [0.5, 186, 14, 2],
    [0.625, 161, 12, 2],
    [0.75, 110, 57, 10],
    [0.875, 158, 145, 145],
    [1, 222, 213, 213],
  ],
  // ColorBrewer Spectral — warm lows (red) → cool highs (blue/violet).
  spectral: [
    [0, 158, 1, 66],
    [0.2, 244, 109, 67],
    [0.4, 254, 224, 139],
    [0.55, 230, 245, 152],
    [0.7, 102, 194, 165],
    [0.85, 50, 136, 189],
    [1, 94, 79, 162],
  ],
  viridis: [
    [0, 68, 1, 84],
    [0.25, 59, 82, 139],
    [0.5, 33, 144, 140],
    [0.75, 93, 201, 99],
    [1, 253, 231, 37],
  ],
  magma: [
    [0, 0, 0, 4],
    [0.25, 80, 18, 123],
    [0.5, 182, 54, 121],
    [0.75, 251, 136, 97],
    [1, 252, 253, 191],
  ],
  inferno: [
    [0, 0, 0, 4],
    [0.25, 87, 16, 109],
    [0.5, 187, 55, 84],
    [0.75, 249, 142, 9],
    [1, 252, 255, 164],
  ],
  plasma: [
    [0, 13, 8, 135],
    [0.25, 126, 3, 168],
    [0.5, 204, 71, 120],
    [0.75, 248, 149, 64],
    [1, 240, 249, 33],
  ],
  turbo: [
    [0, 48, 18, 59],
    [0.25, 70, 107, 227],
    [0.5, 40, 188, 235],
    [0.75, 124, 250, 112],
    [1, 217, 56, 6],
  ],
  terrain: [
    [0, 51, 51, 153],
    [0.2, 0, 255, 255],
    [0.4, 0, 255, 0],
    [0.6, 255, 255, 0],
    [0.8, 255, 127, 0],
    [1, 127, 0, 0],
  ],
  RdBu: [
    [0, 103, 0, 31],
    [0.25, 244, 165, 130],
    [0.5, 247, 247, 247],
    [0.75, 146, 197, 222],
    [1, 5, 48, 97],
  ],
  greyscale: [
    [0, 0, 0, 0],
    [0.5, 128, 128, 128],
    [1, 255, 255, 255],
  ],
};

function clamp01(t: number): number {
  return Math.max(0, Math.min(1, t));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Sample a ramp at t∈[0,1] → `#rrggbb`. */
export function sampleRampColor(ramp: RasterColorRamp, t: number): string {
  const stops = RAMP_RGB_STOPS[ramp] ?? RAMP_RGB_STOPS.viridis;
  const x = clamp01(t);
  if (stops.length === 1) {
    const [, r, g, b] = stops[0];
    return rgbToHex(r, g, b);
  }
  for (let i = 0; i < stops.length - 1; i += 1) {
    const [t0, r0, g0, b0] = stops[i];
    const [t1, r1, g1, b1] = stops[i + 1];
    if (x >= t0 && x <= t1) {
      const u = t1 === t0 ? 0 : (x - t0) / (t1 - t0);
      return rgbToHex(
        Math.round(lerp(r0, r1, u)),
        Math.round(lerp(g0, g1, u)),
        Math.round(lerp(b0, b1, u)),
      );
    }
  }
  const [, r, g, b] = stops[stops.length - 1];
  return rgbToHex(r, g, b);
}

/**
 * A dense explicit 0..255 map gives TiTiler a smooth custom ramp without
 * relying on a server-specific `colormap_type=linear` extension.
 */
export function rampToTitilerColormap(
  ramp: RasterColorRamp,
  colorCount = 256,
): string {
  const count = Math.max(2, Math.min(256, Math.round(colorCount)));
  const map: Record<string, string> = {};
  for (let i = 0; i < count; i += 1) {
    const byte = Math.round((i / (count - 1)) * 255);
    map[String(byte)] = sampleRampColor(ramp, i / (count - 1));
  }
  return JSON.stringify(map);
}

export function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/**
 * Normalize a typed / pasted hex string to `#rrggbb`.
 *
 * Accepts `#RGB`, `#RRGGBB`, and the same without `#`. Returns `null` when the
 * input is incomplete or not hex — callers keep the draft and only commit a
 * successful parse.
 */
export function normalizeHexColor(raw: string): string | null {
  const cleaned = raw.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(cleaned)) {
    return `#${cleaned
      .split('')
      .map((c) => c + c)
      .join('')
      .toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(cleaned)) {
    return `#${cleaned.toLowerCase()}`;
  }
  return null;
}

export function hexToRgba(hex: string): [number, number, number, number] {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return [128, 128, 128, 255];
  const n = Number.parseInt(normalized.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
}

function formatBreakLabel(lo: number, hi: number): string {
  const fmt = (v: number) => {
    if (!Number.isFinite(v)) return '—';
    if (Math.abs(v) >= 1000) return v.toFixed(0);
    if (Math.abs(v) >= 10) return v.toFixed(1);
    return v.toFixed(2);
  };
  return `${fmt(lo)} – ${fmt(hi)}`;
}

function newId(prefix: string, index: number): string {
  return `${prefix}-${index}`;
}

/**
 * Stretch range for TiTiler `rescale`, from ArcGIS Stretch Type + band stats.
 */
export function stretchRangeFromStats(
  stats: RasterBandStatsLike,
  stretchType: RasterStretchType,
  nStdDev = 2,
): [number, number] | null {
  const { min, max } = stats;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;

  switch (stretchType) {
    case 'none':
      return null;
    case 'minmax':
      return [min, max];
    case 'percent-clip': {
      const lo = stats.percentile2 ?? min;
      const hi = stats.percentile98 ?? max;
      return hi > lo ? [lo, hi] : [min, max];
    }
    case 'stddev': {
      const mean = stats.mean ?? (min + max) / 2;
      const std = stats.std ?? (max - min) / 4;
      const lo = Math.max(min, mean - nStdDev * std);
      const hi = Math.min(max, mean + nStdDev * std);
      return hi > lo ? [lo, hi] : [min, max];
    }
    default:
      return [min, max];
  }
}

/**
 * Equal-interval class breaks across [min, max] (ArcGIS Classify / Discrete).
 */
export function buildEqualIntervalBreaks(
  min: number,
  max: number,
  classCount: number,
  ramp: RasterColorRamp,
): RasterClassBreak[] {
  const n = Math.max(2, Math.min(32, Math.round(classCount)));
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return [{
      id: newId('class', 0),
      upperValue: Number.isFinite(max) ? max : 1,
      color: sampleRampColor(ramp, 1),
      label: 'Class 1',
      visible: true,
    }];
  }
  const step = (max - min) / n;
  const breaks: RasterClassBreak[] = [];
  for (let i = 0; i < n; i += 1) {
    const lo = min + step * i;
    const hi = i === n - 1 ? max : min + step * (i + 1);
    breaks.push({
      id: newId('class', i),
      upperValue: hi,
      color: sampleRampColor(ramp, n === 1 ? 1 : i / (n - 1)),
      label: formatBreakLabel(lo, hi),
      visible: true,
    });
  }
  return breaks;
}

/**
 * Quantile breaks from a histogram (counts + bin edges). Falls back to equal
 * interval when the histogram is missing or degenerate.
 */
export function buildQuantileBreaks(
  stats: RasterBandStatsLike,
  classCount: number,
  ramp: RasterColorRamp,
): RasterClassBreak[] {
  const n = Math.max(2, Math.min(32, Math.round(classCount)));
  const { counts, bins, min, max } = stats;
  if (!counts?.length || !bins || bins.length < 2) {
    return buildEqualIntervalBreaks(min, max, n, ramp);
  }

  const total = counts.reduce((s, c) => s + c, 0);
  if (total <= 0) return buildEqualIntervalBreaks(min, max, n, ramp);

  const target = total / n;
  const uppers: number[] = [];
  let cum = 0;
  let classIdx = 0;
  for (let i = 0; i < counts.length; i += 1) {
    cum += counts[i];
    const edge = bins[Math.min(i + 1, bins.length - 1)];
    while (classIdx < n - 1 && cum >= target * (classIdx + 1)) {
      uppers.push(edge);
      classIdx += 1;
    }
  }
  while (uppers.length < n - 1) uppers.push(max);
  uppers.push(max);

  const breaks: RasterClassBreak[] = [];
  let lo = min;
  for (let i = 0; i < n; i += 1) {
    const hi = uppers[i] ?? max;
    breaks.push({
      id: newId('class', i),
      upperValue: hi,
      color: sampleRampColor(ramp, n === 1 ? 1 : i / (n - 1)),
      label: formatBreakLabel(lo, hi),
      visible: true,
    });
    lo = hi;
  }
  return breaks;
}

export function buildClassBreaks(
  stats: RasterBandStatsLike,
  classCount: number,
  method: RasterClassifyMethod,
  ramp: RasterColorRamp,
): RasterClassBreak[] {
  if (method === 'quantile') return buildQuantileBreaks(stats, classCount, ramp);
  return buildEqualIntervalBreaks(stats.min, stats.max, classCount, ramp);
}

/**
 * Recolour existing breaks from a ramp without changing upper values / labels.
 */
export function recolorClassBreaks(
  breaks: readonly RasterClassBreak[],
  ramp: RasterColorRamp,
): RasterClassBreak[] {
  if (breaks.length === 0) return [];
  return breaks.map((row, i) => ({
    ...row,
    color: sampleRampColor(ramp, breaks.length === 1 ? 1 : i / (breaks.length - 1)),
  }));
}

/**
 * Unique-value rows from band stats. Continuous rasters with huge unique
 * counts use histogram bin centres (capped) — same practical limit ArcGIS
 * applies before "Add all values".
 */
export function buildUniqueValueRows(
  stats: RasterBandStatsLike,
  ramp: RasterColorRamp,
  maxRows = 32,
): RasterUniqueValueRow[] {
  const { min, max, counts, bins, uniqueCount } = stats;
  const span = max - min;
  const looksInteger =
    Number.isFinite(min)
    && Number.isFinite(max)
    && Number.isInteger(min)
    && Number.isInteger(max)
    && span >= 0
    && span <= maxRows - 1
    && (uniqueCount == null || uniqueCount <= maxRows);

  if (looksInteger) {
    const rows: RasterUniqueValueRow[] = [];
    const n = Math.round(span) + 1;
    for (let i = 0; i < n; i += 1) {
      const value = min + i;
      rows.push({
        id: newId('uv', i),
        value,
        color: sampleRampColor(ramp, n === 1 ? 1 : i / (n - 1)),
        label: String(value),
        visible: true,
      });
    }
    return rows;
  }

  // Histogram bin midpoints as categorical stand-ins for continuous data.
  if (counts?.length && bins && bins.length >= 2) {
    const ranked = counts
      .map((count, i) => ({
        count,
        value: (bins[i] + bins[Math.min(i + 1, bins.length - 1)]) / 2,
      }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, maxRows)
      .sort((a, b) => a.value - b.value);

    return ranked.map((row, i) => ({
      id: newId('uv', i),
      value: row.value,
      color: sampleRampColor(ramp, ranked.length === 1 ? 1 : i / (ranked.length - 1)),
      label: Number.isInteger(row.value) ? String(row.value) : row.value.toFixed(2),
      visible: true,
    }));
  }

  // Last resort: equal-interval midpoints.
  const n = Math.min(maxRows, 8);
  return buildEqualIntervalBreaks(min, max, n, ramp).map((b, i, arr) => {
    const lo = i === 0 ? min : arr[i - 1].upperValue;
    const value = (lo + b.upperValue) / 2;
    return {
      id: newId('uv', i),
      value,
      color: b.color,
      label: b.label,
      visible: true,
    };
  });
}

/**
 * TiTiler interval colormap JSON for Classify / Discrete.
 * Invisible classes map to fully transparent so they drop out of the tile.
 */
export function classBreaksToTitilerColormap(
  breaks: readonly RasterClassBreak[],
  dataMin: number,
): string {
  if (breaks.length === 0) return '[]';
  const intervals: Array<[[number, number], [number, number, number, number]]> = [];
  let lo = dataMin;
  for (const row of breaks) {
    const hi = row.upperValue;
    const rgba = row.visible ? hexToRgba(row.color) : ([0, 0, 0, 0] as [number, number, number, number]);
    // rio-tiler wants half-open-ish ranges; bump a hair when equal.
    const end = hi <= lo ? lo + Number.EPSILON : hi;
    intervals.push([[lo, end], rgba]);
    lo = end;
  }
  return JSON.stringify(intervals);
}

/** TiTiler discrete colormap JSON for Unique Values (`{"value":"#hex"}`). */
export function uniqueValuesToTitilerColormap(
  rows: readonly RasterUniqueValueRow[],
): string {
  const map: Record<string, string> = {};
  for (const row of rows) {
    if (!row.visible) continue;
    // Integer keys preferred; floats still work as string keys for near-matches
    // only when the raster is categorical. Continuous DEMs should use intervals.
    const key = Number.isInteger(row.value) ? String(row.value) : String(row.value);
    map[key] = row.color;
  }
  return JSON.stringify(map);
}

/**
 * When Unique Values sits on continuous data, prefer interval colormap so every
 * pixel in a bin paints — exact-key maps miss almost all float samples.
 */
export function uniqueValuesToIntervalColormap(
  rows: readonly RasterUniqueValueRow[],
  dataMin: number,
  dataMax: number,
): string {
  if (rows.length === 0) return '[]';
  const sorted = [...rows].sort((a, b) => a.value - b.value);
  const edges: number[] = [dataMin];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    edges.push((sorted[i].value + sorted[i + 1].value) / 2);
  }
  edges.push(dataMax);
  const intervals: Array<[[number, number], [number, number, number, number]]> = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const lo = edges[i];
    const hi = edges[i + 1];
    const rgba = sorted[i].visible
      ? hexToRgba(sorted[i].color)
      : ([0, 0, 0, 0] as [number, number, number, number]);
    intervals.push([[lo, hi <= lo ? lo + Number.EPSILON : hi], rgba]);
  }
  return JSON.stringify(intervals);
}

/** CSS gradient preview for the current class breaks (legend strip). */
export function classBreaksCssGradient(breaks: readonly RasterClassBreak[]): string {
  if (breaks.length === 0) return getRampPreset('viridis').cssGradient;
  const n = breaks.length;
  const stops = breaks.map((b, i) => {
    const a = (i / n) * 100;
    const c = ((i + 1) / n) * 100;
    return `${b.color} ${a}% ${c}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
